import React from 'react';

import PropTypes from 'prop-types';
import styled, { css, createGlobalStyle } from 'styled-components';

import { ImageAsset } from '@techstyle/react-assets';
import { useBreakpoint } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { desktop, mobile } from '../../styles';
import { BasicModal } from '../../styles/modal';
import { Component as CloseButton } from '../CloseButton';
import { Component as ModalCloseButton } from '../ModalCloseButton';
import ToggleTabs from '../ToggleTabs/ToggleTabs';

const ModalTitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 51px;
  max-height: 51px;
  background: ${({ theme }) => theme.colors.lavender250};
`;

const ModalTitle = styled.h1`
  color: ${props => props.theme.colors.default};
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0.3px;
`;

const tabDialogStyle = css`
  text-align: center;
  border-radius: 2px;
  min-height: 647px;
  width: 90%;

  ${desktop`
    width: 628px;
  `};
`;

const dialogStyle = css`
  padding: 0 ${props => props.theme.spacing.small}px;

  ${mobile`
    width: 100%;
    padding-bottom: ${props => props.theme.spacing.large}px;
  `};
`;

const SizeGuideModalStyles = createGlobalStyle`
  /* Force no scrolling of content underneath modal on Mobile Safari.
   * See: https://gist.github.com/claus/621a938d21d80f367251dc2eaaa1b2a9
   * This also fixes weird misalignment of cursor when typing into the inputs on Mobile Safari.
   */
  @media (max-width: 1022px) {
    .SizeGuideModal {
      position: fixed;
    }
  }
`;

const SpecificProductSizeGuideModalStyles = createGlobalStyle`
  /* Force no scrolling of content underneath modal on Mobile Safari.
   * See: https://gist.github.com/claus/621a938d21d80f367251dc2eaaa1b2a9
   * This also fixes weird misalignment of cursor when typing into the inputs on Mobile Safari.
   */
  @media (max-width: 1022px) {
    .SpecificProductSizeGuideModal {
      position: fixed;
    }
  }
`;

const overlayStyle = css`
  background: ${props => props.theme.colors.scrim};
`;

const StyledModal = styled(BasicModal)`
  ${desktop`
    ${({ $isOpen }) => $isOpen && 'z-index: 1201;'}  
  `}
`;

const TabbedStyledModal = styled(BasicModal).attrs(props => {
  return {
    overlayColor: props.theme.colors.scrim,
    ...props,
  };
})`
  ${desktop`
    ${({ $isOpen }) => $isOpen && 'z-index: 1201;'}  
  `}
`;

const StyledCloseButton = styled(CloseButton)`
  width: 51px;
  height: 51px;
  line-height: 0px;
`;

const StyledModalCloseButton = styled(ModalCloseButton)`
  ${mobile`
    bottom: 0px;
    line-height: 13px;
    height: 50px;
    width: 50px;
  `}
`;

const StyledToggleTabs = styled(ToggleTabs)`
  ${ToggleTabs.TabButton} {
    --active-color: ${({ theme }) => theme.colors.active};
    --active-border-color: ${({ theme }) => theme.colors.active};
    align-items: center;
    cursor: pointer;
    display: inline-flex;
    flex-direction: column;
    font-size: 10px;
    letter-spacing: 1px;
    height: 50px;
    padding: 0px;
    transition: border-color 0.3s ease-in, color 0.3s ease-in;
    width: 100px;

    ${desktop`
      width: 150px;
`}
  }

  ${ToggleTabs.TabButton}:hover {
    border-color: var(--active-color);
    font-weight: 700;
    color: var(--active-border-color);
  }

  /* Hack to prevent tab from shifting when changing font-weight on hover */
  ${ToggleTabs.TabButton}::after {
    content: attr(data-text);
    height: 0;
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    font-weight: 700;
  }

  ${ToggleTabs.TabButtonsContainer} {
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.tiny}px;
  }

  ${ToggleTabs.ContentContainer} {
    max-height: 538px;
    overflow-y: scroll;
    --webkit-overflow-scrolling: touch;
  }
`;

const FullWidthImage = styled(ImageAsset)`
  width: 100%;
`;

const SizeGuideModal = ({
  assets = [],
  children,
  isOpen,
  isSpecificProduct = false,
  isTabbed = false,
  modalMaxHeight,
  onClose,
  selectedTabSizeCategory,
  title,
  titleId,
}) => {
  let tabsData = [];
  const { isMobile } = useBreakpoint();

  if (assets) {
    tabsData = assets.map(asset => {
      const { options } = asset;
      return {
        name: options.customVars.categoryLabel,
        tabTitle: options.customVars.categoryLabel,
        sizeGuideCategory: options.customVars.sizeGuideCategory,
        asset,
      };
    });
  }

  const renderTab = selectedTab => {
    return (
      <FullWidthImage
        asset={selectedTab.asset}
        alt={`Size Guide for ${selectedTab.name}`}
        lazyLoad
      />
    );
  };

  const renderTabSizes = () => {
    const defaultTabIndex = selectedTabSizeCategory
      ? tabsData.findIndex(tabData => {
          let categoryTabToSelect = selectedTabSizeCategory;
          if (
            selectedTabSizeCategory === 'Lingerie' ||
            selectedTabSizeCategory === 'Sleep'
          ) {
            categoryTabToSelect = 'LingerieSleep';
          }
          return (
            tabData.sizeGuideCategory.toLowerCase() ===
            categoryTabToSelect.toLowerCase()
          );
        })
      : 0;

    return (
      <>
        <ModalTitleWrapper>
          <ModalTitle>
            <FormattedMessage
              id="site_account_profile.size_guide_modal"
              defaultMessage="Size Guide"
            />
          </ModalTitle>
        </ModalTitleWrapper>
        <StyledToggleTabs
          tabs={tabsData}
          defaultTabIndex={defaultTabIndex >= 0 ? defaultTabIndex : 0}
        >
          {({ selectedTab }) => renderTab(selectedTab)}
        </StyledToggleTabs>
      </>
    );
  };

  if (isTabbed) {
    return (
      <TabbedStyledModal
        $isOpen={isOpen}
        title={title}
        isOpen={isOpen}
        onExit={onClose}
        overlayStyle={overlayStyle}
        dialogStyle={tabDialogStyle}
        titleId={titleId}
        closeButton={<StyledCloseButton iconSize="12" />}
        transitionFrom={{
          transform: 'scale(0)',
          opacity: 0,
        }}
        transitionEnter={{
          transform: 'scale(1)',
          opacity: 1,
        }}
        transitionLeave={{
          transform: 'scale(0)',
          opacity: 0,
        }}
        springConfig={{
          tension: 170,
          friction: 26,
        }}
      >
        {renderTabSizes()}
      </TabbedStyledModal>
    );
  }

  const modalCloseButton = isMobile ? (
    <StyledModalCloseButton
      iconSize="14"
      data-autotag="guideSizeModalCloseButton"
    />
  ) : (
    <StyledModalCloseButton data-autotag="guideSizeModalCloseButton" />
  );

  return (
    <>
      <StyledModal
        $isOpen={isOpen}
        title={title}
        isOpen={isOpen}
        onExit={onClose}
        dialogStyle={dialogStyle}
        titleId={titleId}
        closeButton={modalCloseButton}
        transitionFrom={{
          transform: 'scale(0)',
          opacity: 0,
        }}
        transitionEnter={{
          transform: 'scale(1)',
          opacity: 1,
          ...(modalMaxHeight && { maxHeight: modalMaxHeight }),
        }}
        transitionLeave={{
          transform: 'scale(0)',
          opacity: 0,
        }}
        springConfig={{
          tension: 170,
          friction: 26,
        }}
      >
        {children}
      </StyledModal>
      {isSpecificProduct ? (
        <SpecificProductSizeGuideModalStyles />
      ) : (
        <SizeGuideModalStyles />
      )}
    </>
  );
};

SizeGuideModal.propTypes = {
  assets: PropTypes.array,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isOpen: PropTypes.bool,
  isTabbed: PropTypes.bool,
  isSpecificProduct: PropTypes.bool,
  modalMaxHeight: PropTypes.string,
  onClose: PropTypes.func,
  selectedTabSizeCategory: PropTypes.string,
  title: PropTypes.string,
  titleId: PropTypes.string,
};

SizeGuideModal.StyledModal = StyledModal;
export default SizeGuideModal;
